import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '../primitives/Badge';
import { cn } from '../utils/cn';
import { useDismissable } from '../utils/useDismissable';
import { useTheme } from '../theme/useTheme';
import { useNotifications } from './useNotifications';
const DEFAULT_LABELS = {
    de: {
        open: 'Benachrichtigungen öffnen',
        title: 'Benachrichtigungen',
        empty: 'Keine neuen Benachrichtigungen.',
        markAllRead: 'Alle als gelesen markieren',
        clear: 'Alle löschen',
    },
    en: {
        open: 'Open notifications',
        title: 'Notifications',
        empty: 'No new notifications.',
        markAllRead: 'Mark all as read',
        clear: 'Clear all',
    },
};
const SEVERITY_CLASS = {
    info: 'sec-notif-item__dot--info',
    success: 'sec-notif-item__dot--success',
    warning: 'sec-notif-item__dot--warning',
    error: 'sec-notif-item__dot--error',
};
function BellIcon() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" }), _jsx("path", { d: "M10.3 21a1.94 1.94 0 0 0 3.4 0" })] }));
}
function CheckIcon() {
    return (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M20 6 9 17l-5-5" }) }));
}
function TrashIcon() {
    return (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" }) }));
}
function formatTime(ts, lang) {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(lang);
}
/**
 * App-uebergreifendes Benachrichtigungs-Center (Saeule B). Quelle-agnostisch:
 * liest aus dem `NotificationProvider`. Bell-Button mit Unread-Badge, Popover
 * mit Liste; Klick markiert gelesen und ruft `onSelect` (Deep-Link).
 * Optik: `.sec-notif-*`-Klassen des Design-Systems.
 */
export function NotificationCenter({ onSelect, labels, className }) {
    const { items, unreadCount, markRead, markAllRead, clear } = useNotifications();
    const { language } = useTheme();
    const l = { ...DEFAULT_LABELS[language], ...labels };
    // Pfeiltasten/Home/End wandern ueber die Eintraege, Escape schliesst und
    // gibt den Fokus an die Glocke zurueck; Tab erreicht weiterhin die
    // Werkzeug-Buttons. Ein leeres Panel bekommt den Fokus selbst (tabindex -1).
    const { open, toggle, containerRef } = useDismissable(false, {
        rovingFocus: true,
        itemSelector: '.sec-notif-item',
        closeOnTab: false,
    });
    const onItemClick = (item) => {
        if (!item.read)
            markRead(item.id);
        onSelect?.(item);
    };
    return (_jsxs("div", { ref: containerRef, className: cn('sec-notif-center', className), children: [_jsxs("button", { type: "button", className: "sec-icon-btn sec-notif-center__trigger", "aria-haspopup": "dialog", "aria-expanded": open, "aria-label": unreadCount > 0 ? `${l.open} (${unreadCount})` : l.open, onClick: toggle, children: [_jsx(BellIcon, {}), unreadCount > 0 ? (_jsx(Badge, { variant: "coral", className: "sec-notif-center__count", children: unreadCount > 99 ? '99+' : unreadCount })) : null] }), open ? (_jsxs("div", { className: "sec-menu sec-notif-panel", role: "dialog", "aria-label": l.title, tabIndex: -1, children: [_jsxs("header", { className: "sec-notif-panel__header", children: [_jsx("strong", { className: "sec-notif-panel__title", children: l.title }), _jsxs("span", { className: "sec-notif-panel__tools", children: [_jsx("button", { type: "button", className: "sec-icon-btn sec-icon-btn--sm", onClick: markAllRead, disabled: unreadCount === 0, title: l.markAllRead, "aria-label": l.markAllRead, children: _jsx(CheckIcon, {}) }), _jsx("button", { type: "button", className: "sec-icon-btn sec-icon-btn--sm", onClick: clear, disabled: items.length === 0, title: l.clear, "aria-label": l.clear, children: _jsx(TrashIcon, {}) })] })] }), items.length === 0 ? (_jsx("p", { className: "sec-muted sec-notif-panel__empty", children: l.empty })) : (_jsx("ul", { className: "sec-notif-list", children: items.map((item) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => onItemClick(item), className: cn('sec-notif-item', !item.read && 'sec-notif-item--unread'), children: [_jsx("span", { "aria-hidden": "true", className: cn('sec-notif-item__dot', SEVERITY_CLASS[item.severity]) }), _jsxs("span", { className: "sec-notif-item__body", children: [_jsx("span", { className: "sec-notif-item__title", children: item.title }), item.body ? _jsx("span", { className: "sec-notif-item__text", children: item.body }) : null, _jsx("span", { className: "sec-notif-item__meta", children: [item.source, formatTime(item.timestamp, language)].filter(Boolean).join(' · ') })] })] }) }, item.id))) }))] })) : null] }));
}
